import React from 'react';

import { createRoot } from 'react-dom/client';

import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { type DisplaySettings } from '@client/retailMedia/RetailMediaAdVendor.js';
import { i18n } from '@lingui/core';
import {
  AdServer,
  ContainerId,
  type PlacementId,
  PlacementStatus,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductAd } from './ads/SponsoredProductAd.js';
import {
  DsaBrand,
  type RetailMediaPayload,
  type RetailMediaProduct
} from './types/index.js';
import {
  callApi,
  RateLimitError,
  getVisitorId
} from './utils/RetailMediaApi.js';

const request = async (
  placementId: PlacementId,
  searchQuery: string,
  categories: string[] | undefined,
  displaySettings?: DisplaySettings,
  brandNames?: Record<string, string>
) => {
  try {
    const brand = $config.get()?.brand;
    const dsaBrand = DsaBrand.is(brand) ? brand : undefined;

    const payload: RetailMediaPayload = {
      zone: 'recommerce_search',
      visitorId: getVisitorId(),
      sponsoredProductsCount: 1,
      searchTerm: searchQuery,
      ...(categories && categories.length > 0 && { categories }),
      ...(brandNames &&
        Object.keys(brandNames).length > 0 && {
          brandNames: Object.values(brandNames)
        }),
      debug: true,
      userAgent: navigator.userAgent,
      ...(dsaBrand && { dsaBrand })
    };

    updatePlacementKeyValueById(placementId, 'creativeType', 'native-retail');
    updatePlacementKeyValueById(placementId, 'status', 'request');

    const dataPromise = callApi(payload);

    debugLog(
      `retail_media -> ad requested for placement: ${placementId}`,
      payload
    );

    updatePlacementKeyValueById(placementId, 'status', 'requested');
    updateMetricByKey(placementId, PlacementStatus.AdRequested);

    const data = await dataPromise;
    debugLog('retail_media -> API response:', data);

    setTimeout(() => {
      try {
        renderSponsoredListingAd(
          placementId,
          data.sponsoredProducts,
          displaySettings,
          categories
        );
      } catch (error) {
        debugLog(
          'retail_media -> Error rendering sponsored listing ad:',
          error
        );

        updateMetricByKey(placementId, PlacementStatus.AdNoFill);
        updatePlacementKeyValueById(placementId, 'status', 'error');
      }
    }, 1);
  } catch (error) {
    // Ignore rate-limited requests - the first request will handle rendering
    if (error instanceof RateLimitError) {
      debugLog('retail_media -> Rate-limited request ignored:', error.message);
      return;
    }

    debugLog('retail_media -> Error in request:', error);
    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');
  }
};

export function triggerRequest(
  placementId: PlacementId,
  searchQuery: string | string[] | undefined,
  categories: string[] | undefined,
  displaySettings?: DisplaySettings,
  brandNames?: Record<string, string>
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  void request(
    placementId,
    normalizedQuery || '',
    categories,
    displaySettings,
    brandNames
  );
}

function renderSponsoredListingAd(
  placementId: PlacementId,
  products?: Array<RetailMediaProduct>,
  displaySettings?: DisplaySettings,
  categories?: string[]
) {
  const containerId = ContainerId.fromPlacementId(
    placementId,
    AdServer.Retailmedia
  );

  const containerElement = document.getElementById(containerId);

  if (!containerElement) {
    debugLog('retail_media -> Container element not found:', containerId);
    updateMetricByKey(placementId, PlacementStatus.NoElementInDom);
    updatePlacementKeyValueById(placementId, 'status', 'error');
    return;
  }

  // Clear the slot
  containerElement.innerHTML = '';
  containerElement.style.gridColumn = '1 / 1';

  // Handle no-fill case
  if (!products?.length) {
    debugLog('retail_media -> No-fill case for placement:', placementId);
    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');
    return;
  }

  const originalLocale = i18n.locale;

  const supportedLocale = ['fi', 'nb', 'da', 'sv'].includes(i18n.locale)
    ? i18n.locale
    : 'nb'; // Default to Norwegian

  if (originalLocale !== supportedLocale) {
    i18n.activate(supportedLocale);
  }

  // Create a container for the React component
  const container = document.createElement('div');
  containerElement.appendChild(container);

  // Render the React component into the container
  const root = createRoot(container);

  root.render(
    <SponsoredProductAd
      displaySettings={displaySettings}
      products={products}
      placementId={placementId}
      categories={categories}
    />
  );

  if (originalLocale !== supportedLocale) {
    setTimeout(() => {
      i18n.activate(originalLocale);
    }, 0);
  }
}
